import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchGymDashboard, getCurrency, getUser } from "../services/api";
import { formatPrice } from "../utils/currency";

// Owner dashboard for a spa / gym venue. First screen a wellness-venue manager
// sees: membership health (active / new / expiring / overdue), month-to-date
// revenue, and today's bookings — plus quick actions into the wellness module.

const S = {
  en: {
    title: "Gym & Spa overview", sub: "Your membership and booking health at a glance.",
    check_in: "Check-in", checkins: "Check-ins today",
    active: "Active members", new_m: "New this month", expiring: "Expiring in 14 days",
    overdue: "Overdue members", rev_month: "Revenue this month", dues: "Membership dues", services: "Services",
    exp_title: "Expiring soon", exp_empty: "No memberships expiring in the next 2 weeks.",
    due_in: "in {n}d", today_title: "Today's bookings", today_empty: "No bookings today.",
    view_all: "View all", members: "Members", add_member: "Add member", schedule: "Schedule",
    new_booking: "New booking", arrears: "owed", plan: "Plan", renew: "Renew",
  },
  am: {
    title: "የጅም እና ስፓ አጠቃላይ እይታ", sub: "የአባልነት እና ቦታ ማስያዝ ሁኔታዎ በአንድ እይታ።",
    check_in: "መግቢያ", checkins: "የዛሬ መግቢያዎች",
    active: "ንቁ አባላት", new_m: "በዚህ ወር አዲስ", expiring: "በ14 ቀናት የሚያልቅ",
    overdue: "ጊዜ ያለፈባቸው አባላት", rev_month: "የዚህ ወር ገቢ", dues: "የአባልነት ክፍያ", services: "አገልግሎቶች",
    exp_title: "በቅርቡ የሚያልቅ", exp_empty: "በሚቀጥሉት 2 ሳምንታት የሚያልቅ አባልነት የለም።",
    due_in: "በ{n}ቀን", today_title: "የዛሬ ቦታ ማስያዣዎች", today_empty: "ዛሬ ቦታ ማስያዝ የለም።",
    view_all: "ሁሉንም ይመልከቱ", members: "አባላት", add_member: "አባል ጨምር", schedule: "መርሃግብር",
    new_booking: "አዲስ ማስያዝ", arrears: "ዕዳ", plan: "ዕቅድ", renew: "አድስ",
  },
  om: {
    title: "Ilaalcha Jimii fi Spaa", sub: "Fayyaa miseensummaa fi buukiingii kee ilaalcha tokkoon.",
    check_in: "Galmee", checkins: "Galmee har'aa",
    active: "Miseensota sochii", new_m: "Ji'a kana haaraa", expiring: "Guyyaa 14 keessatti xumuramu",
    overdue: "Miseensota yeroon darbe", rev_month: "Galii ji'a kana", dues: "Kaffaltii miseensummaa", services: "Tajaajiloota",
    exp_title: "Dhiyootti xumuramu", exp_empty: "Torban 2 dhufan keessatti miseensummaan xumuramu hin jiru.",
    due_in: "guyyaa {n}", today_title: "Buukiingii har'aa", today_empty: "Har'a buukiingiin hin jiru.",
    view_all: "Hunda ilaali", members: "Miseensota", add_member: "Miseensa dabali", schedule: "Sagantaa",
    new_booking: "Buukiingii haaraa", arrears: "idaa", plan: "Karoora", renew: "Haaromsi",
  },
};

export default function GymDashboardPage() {
  const lang = (typeof window !== "undefined" && localStorage.getItem("lang")) || "en";
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (n) => formatPrice(Number(n || 0), cur);
  const user = getUser() || {};

  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchGymDashboard().then(setD).catch((e) => setErr(e?.response?.data?.detail || "Could not load the dashboard."))
      .finally(() => setLoading(false));
  }, []);

  const hr = new Date().getHours();
  const greet = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";

  const card = (label, value, opts = {}) => (
    <div style={{ flex: "1 1 150px", background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: "14px 16px", ...opts.box }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 2, color: opts.color || "#1f2d3d" }}>{value}</div>
      {opts.sub && <div className="muted" style={{ fontSize: 11 }}>{opts.sub}</div>}
    </div>
  );

  return (
    <div className="page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ marginBottom: 2 }}>🏋️ {t("title")}</h1>
          <p className="muted" style={{ marginTop: 0 }}>{greet}{user.name ? `, ${user.name}` : ""} · {t("sub")}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link className="btn btn-primary" to="/checkin" style={{ textDecoration: "none" }}>🎫 {t("check_in")}</Link>
          <Link className="btn" to="/members" style={{ textDecoration: "none" }}>＋ {t("add_member")}</Link>
          <Link className="btn" to="/service-schedule" style={{ textDecoration: "none" }}>{t("new_booking")}</Link>
        </div>
      </div>

      {err && <div style={{ background: "#fdecec", color: "#b02a2a", padding: "8px 12px", borderRadius: 8, margin: "10px 0" }}>{err}</div>}
      {loading && <div className="muted" style={{ marginTop: 16 }}>…</div>}

      {d && (
        <>
          {/* KPI cards */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "14px 0" }}>
            {card(t("checkins"), d.checkins_today != null ? d.checkins_today : "—")}
            {card(t("active"), d.active_members)}
            {card(t("new_m"), d.new_members_this_month, { color: "#2b8a3e" })}
            {card(t("expiring"), d.expiring_soon_count, { color: d.expiring_soon_count ? "#b8860b" : "#1f2d3d" })}
            {card(t("overdue"), d.overdue_count, { color: d.overdue_count ? "#b02a2a" : "#1f2d3d", sub: d.overdue_arrears ? `${money(d.overdue_arrears)} ${t("arrears")}` : "" })}
            {card(t("rev_month"), money(d.revenue_this_month), { box: { background: "#f2fbf4", borderColor: "#cdeed5" }, color: "#2b8a3e", sub: `${t("dues")} ${money(d.dues_this_month)} · ${t("services")} ${money(d.services_revenue_this_month)}` })}
          </div>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
            {/* Expiring soon */}
            <div style={{ flex: "1 1 340px", background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>⏳ {t("exp_title")}</h3>
                <Link className="muted" to="/renewals" style={{ fontSize: 13 }}>{t("view_all")} →</Link>
              </div>
              {(!d.expiring_soon || d.expiring_soon.length === 0) && <p className="muted" style={{ marginBottom: 0 }}>{t("exp_empty")}</p>}
              {d.expiring_soon && d.expiring_soon.map((m) => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: "1px solid #f2f4f6" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{m.name || "—"}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{m.plan} · {money(m.dues_amount)}</div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: m.due_in_days <= 3 ? "#b02a2a" : "#b8860b" }}>
                    {t("due_in").replace("{n}", m.due_in_days)}
                  </span>
                </div>
              ))}
            </div>

            {/* Today's bookings */}
            <div style={{ flex: "1 1 340px", background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>📅 {t("today_title")} {d.bookings_today_count ? `(${d.bookings_today_count})` : ""}</h3>
                <Link className="muted" to="/service-schedule" style={{ fontSize: 13 }}>{t("schedule")} →</Link>
              </div>
              {(!d.bookings_today || d.bookings_today.length === 0) && <p className="muted" style={{ marginBottom: 0 }}>{t("today_empty")}</p>}
              {d.bookings_today && d.bookings_today.map((b) => (
                <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: "1px solid #f2f4f6" }}>
                  <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, minWidth: 48 }}>{b.time || "—"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{b.name || "—"}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{b.service || ""}{b.area ? ` · ${b.area}` : ""}</div>
                  </div>
                  <span className="muted" style={{ fontSize: 11 }}>{b.status}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
